import { useEffect, useRef, useState } from "react";
import { api } from "../api/client";
import type { Release, StreamSet } from "../api/types";
import { Modal } from "./Modal";
import { pitchWithRate, registerAudio, stopEmbed } from "./player";

/**
 * A player with a ±16% fader — the range of a 1210 — so you can hear what a
 * record does pitched.
 *
 * It cannot use Bandcamp's player. That embed is a cross-origin iframe: the
 * page cannot reach the <audio> inside it to set playbackRate, and there is no
 * postMessage API to ask. So this is an <audio> of our own, pointed at the same
 * 128 kbps preview the bpm stage measures. Rate shifts pitch along with tempo,
 * which is what a turntable fader does and what is wanted here.
 *
 * The URLs are signed and last 24 hours, which is why the old published page
 * could only offer this on releases fetched that day. Here they are fetched at
 * the moment the popup opens, so nothing expires and every playable release
 * has the button.
 *
 * Where Bandcamp streams nothing the set is the shop's own clips, and the
 * fader works on those exactly as well -- an MP3 is an MP3. What it cannot
 * pitch is an embedded player (a YouTube or Bandcamp iframe a Shopify shop
 * pasted into a description), so those are left out of the picker here.
 */
export function SpeedTestModal({ release, startPos, onClose }: {
  release: Release;
  /** Open on this track. A position within the release, not an index into the
   *  stream list: those differ, because the stream list holds only the tracks
   *  Bandcamp will actually serve. */
  startPos?: number;
  onClose: () => void;
}) {
  const [set, setSet] = useState<StreamSet | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pick, setPick] = useState(0);
  // The fader position survives switching tracks: you are comparing them at a
  // pitch, and having it snap back to zero each time would defeat the point.
  const [pct, setPct] = useState(0);
  const audio = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    let alive = true;
    stopEmbed();
    api.streams(release.key)
      .then((s) => {
        if (!alive) return;
        const audio = (s.tracks ?? []).filter((t) => t.kind !== "embed");
        setSet({ ...s, tracks: audio });
        // Open on the track that was asked for, where it is streamable at all.
        // Falling back to the first is right: a request for a track Bandcamp
        // will not serve should still give a working popup rather than an
        // empty one.
        const i = startPos == null ? -1 : audio.findIndex((t) => t.pos === startPos);
        setPick(i >= 0 ? i : 0);
      })
      .catch((e: Error) => alive && setError(e.message));
    return () => { alive = false; };
  }, [release.key, startPos]);

  // Starting a Bandcamp embed should silence this, and vice versa.
  useEffect(() => registerAudio(() => audio.current?.pause()), []);

  // playbackRate is a property of the loaded media, not of the element, so it
  // resets to 1 every time the src changes -- and switching track changes the
  // src. Applying it on load as well as on change is what keeps the fader
  // where you left it when you move between tracks.
  const rate = 1 + pct / 100;
  useEffect(() => {
    if (audio.current) pitchWithRate(audio.current, rate);
  }, [rate, pick, set]);

  const track = set?.tracks[pick];
  const bpm = track?.bpm;
  const shifted = bpm ? (bpm * (1 + pct / 100)).toFixed(1) : null;

  return (
    <Modal title={release.title} who={release.artist} onClose={onClose}
           meta={set?.source
             ? <>clips from {set.source.url
                 ? <a href={set.source.url} target="_blank" rel="noopener noreferrer">{set.source.label}</a>
                 : set.source.label}</>
             : set?.fresh ? "fresh URLs" : undefined}>
      <div className="sp">
        {error && <div className="note">{error}</div>}
        {!set && !error && <div className="note">fetching a preview…</div>}

        {set && set.tracks.length === 0 && (
          <div className="note">
            {set.source
              ? `${set.source.label} only embeds another site's player for this record, and an embedded player cannot be pitched.`
              : "Neither Bandcamp nor any shop carrying this record streams a clip of it."}
          </div>
        )}

        {track && (
          <>
            <div className="pick">
              {set!.tracks.map((t, i) => (
                <button key={t.pos} className={i === pick ? "on" : ""}
                        onClick={() => setPick(i)} title={t.title}>
                  {t.label ?? t.n ?? t.pos}. {t.title.length > 28 ? t.title.slice(0, 27) + "…" : t.title}
                </button>
              ))}
            </div>

            <audio ref={audio} src={track.url} controls preload="none"
                   onLoadedMetadata={(e) => { pitchWithRate(e.currentTarget, rate); }}
                   onPlay={() => stopEmbed()} />

            <div className="fader">
              <button className="zero" onClick={() => setPct(0)}>0%</button>
              <input type="range" min={-16} max={16} step={0.1} value={pct}
                     onChange={(e) => setPct(Number(e.target.value))} />
              <span className="read">
                <b>{pct > 0 ? "+" : ""}{pct.toFixed(1)}%</b>
                {shifted && <> · <span className="bpm">{shifted} BPM</span></>}
              </span>
            </div>
            {!bpm && (
              <div className="note" style={{ padding: "10px 0 0", border: 0 }}>
                No BPM measured for this track, so the readout shows the fader only.
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
